import { Box, Chip, FormControlLabel, FormGroup, Paper, Stack, Switch, Typography, alpha } from '@mui/material';
import { filter, map, size, values } from 'lodash';
import React from 'react';

import { StudyWorkflowConfiguration, StudyWorkflowConfigurationKey } from '../../Workflow/studyWorkflowConfiguration';

interface Props {
  configuration: StudyWorkflowConfiguration;
  onChange: (key: StudyWorkflowConfigurationKey, enabled: boolean) => void;
  scope?: 'study' | 'lab-defaults';
  description?: string;
}

interface WorkflowToggleDefinition {
  key: StudyWorkflowConfigurationKey;
  label: string;
  description: string;
}

const workflowToggleDefinitions: WorkflowToggleDefinition[] = [
  {
    key: 'rnaProbes',
    label: 'RNA probes',
    description: 'Adds RNA marker ranges, spot detection, peak review, and RNA feature calculation.',
  },
  {
    key: 'markerPositivity',
    label: 'Marker positivity',
    description: 'Adds model inference and thresholding to determine marker positivity.',
  },
  {
    key: 'cellRulesBasedCellTyping',
    label: 'Cell rules-based cell typing',
    description: 'Adds cell rules, cell-typing model fitting, cell-type inference, and model-performance QA.',
  },
];

export const WorkflowConfigurationPanel: React.FC<Props> = ({
  configuration,
  onChange,
  scope = 'study',
  description,
}) => {
  const enabledModuleCount = size(filter(values(configuration), Boolean));

  return (
    <Paper
      component="section"
      aria-labelledby="workflow-configuration-heading"
      variant="outlined"
      sx={{
        p: { xs: 2, md: 2.5 },
        borderRadius: (theme) => `${theme.shape.borderRadius}px`,
      }}
    >
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
        >
          <Stack spacing={0.4}>
            <Typography id="workflow-configuration-heading" variant="subtitle2">
              Configure workflow modules
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {description ||
                (scope === 'lab-defaults'
                  ? 'Choose the modules to apply to new studies in this lab. A study-level setting always takes precedence.'
                  : "Choose the modules for this study's workflow. Select Save to apply your changes.")}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            <Chip size="small" label={`${enabledModuleCount} of ${workflowToggleDefinitions.length} enabled`} />
          </Stack>
        </Stack>

        <FormGroup
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, minmax(0, 1fr))' },
            gap: 1.5,
          }}
        >
          {map(workflowToggleDefinitions, ({ key, label, description: toggleDescription }) => {
            const checked = configuration[key];
            const cellTypingNeedsMarkerPositivity =
              key === 'cellRulesBasedCellTyping' && !configuration.markerPositivity;

            return (
              <Box
                key={key}
                sx={(theme) => ({
                  p: 1.5,
                  borderRadius: `${theme.shape.borderRadius}px`,
                  border: `1px solid ${alpha(
                    checked ? theme.palette.primary.main : theme.palette.divider,
                    checked ? 0.46 : 0.8
                  )}`,
                  backgroundColor: checked ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
                  transition: theme.transitions.create(['border-color', 'background-color'], {
                    duration: theme.transitions.duration.shorter,
                  }),
                })}
              >
                <FormControlLabel
                  sx={{ m: 0, width: '100%', alignItems: 'flex-start' }}
                  control={
                    <Switch
                      checked={checked}
                      onChange={(event) => onChange(key, event.target.checked)}
                      inputProps={{ 'aria-label': label }}
                    />
                  }
                  label={
                    <Stack spacing={0.35} sx={{ pt: 0.75 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {toggleDescription}
                      </Typography>
                      {cellTypingNeedsMarkerPositivity && (
                        <Typography variant="caption" color="primary.main">
                          Enabling this also enables marker positivity.
                        </Typography>
                      )}
                    </Stack>
                  }
                />
              </Box>
            );
          })}
        </FormGroup>
      </Stack>
    </Paper>
  );
};
